import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import "@/App.css";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Sprout, Menu, X, Settings, LogOut, ShieldCheck, Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import MapView from "@/components/MapView";
import Sidebar from "@/components/Sidebar";
import { parseFile } from "@/lib/parseData";
import { geocodeParcels } from "@/lib/cadastre";
import { assignCropUsing, polygonCentroid, pointInPolygon, mergeHistory } from "@/lib/geo";
import { exportPDF, exportExcel, downloadTemplate, exportParcelSheet, exportOwnerSheet } from "@/lib/exporters";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [parcels, setParcels] = useState([]);
  const [showAerial, setShowAerial] = useState(true);
  const [showCadastre, setShowCadastre] = useState(true);
  const [showParcels, setShowParcels] = useState(true);
  const [showCultures, setShowCultures] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total,label}
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [farmName, setFarmName] = useState(user?.farm_name || "");
  const [drawParcel, setDrawParcel] = useState(null);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [campaignDialog, setCampaignDialog] = useState(null); // { parsed, year }
  const [historyParcel, setHistoryParcel] = useState(null);
  const [histEntries, setHistEntries] = useState([]);
  const [histYear, setHistYear] = useState("");
  const [histCrop, setHistCrop] = useState("");
  const didFit = useRef(false);

  const owners = useMemo(() => {
    const map = new Map();
    parcels
      .filter((p) => p.source !== "xml" && p.owner)
      .forEach((p) => {
        const o = map.get(p.owner) || { name: p.owner, count: 0, area: 0 };
        o.count += 1;
        o.area += p.area || 0;
        map.set(p.owner, o);
      });
    return Array.from(map.values()).map((o) => ({ ...o, area: Math.round(o.area * 100) / 100 }));
  }, [parcels]);

  useEffect(() => {
    api
      .get("/parcels")
      .then((r) => {
        setParcels(r.data);
        if (!didFit.current && r.data.some((p) => p.polygon)) {
          didFit.current = true;
          setFitTrigger((t) => t + 1);
        }
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const processImport = useCallback(
    async (parsed, campaign) => {
      setImporting(true);
      try {
        const isXmlImport = parsed.some((p) => p.source === "xml");
        if (isXmlImport && campaign) {
          parsed.forEach((p) => {
            if (p.source === "xml") p.campaign = campaign;
          });
        }

        // Géocodage cadastre des parcelles Excel sans géométrie
        const toGeo = parsed.filter((p) => p.needsGeometry);
        if (toGeo.length) {
          setProgress({ done: 0, total: toGeo.length, label: "Géolocalisation cadastre" });
          await geocodeParcels(parsed, (done, total) =>
            setProgress({ done, total, label: "Géolocalisation cadastre" })
          );
          setProgress(null);
        }

        // Fusion culture : Excel enrichi par les parcelles Télépac déjà chargées
        if (!isXmlImport) {
          const xmlSource = parcels.filter((p) => p.source === "xml");
          if (xmlSource.length) assignCropUsing(parsed, xmlSource);
        }

        const { data } = await api.post("/parcels/import", { parcels: parsed, replace: false });
        let finalList = data;

        // Si on vient d'importer un XML : enrichir rétroactivement les parcelles cadastrales existantes
        if (isXmlImport) {
          const xmlAll = data.filter((p) => p.source === "xml");
          const changed = [];
          finalList = data.map((p) => {
            if (p.source === "xml" || !p.polygon || p.polygon.length < 3) return p;
            const c = polygonCentroid(p.polygon);
            const m = c && xmlAll.find((x) => x.polygon && pointInPolygon(c, x.polygon));
            if (m && m.crop) {
              const label = m.crop_label || m.crop;
              const hist = campaign
                ? mergeHistory(p.crop_history, { year: campaign, crop: m.crop, crop_label: label })
                : p.crop_history || [];
              const histChanged = JSON.stringify(hist) !== JSON.stringify(p.crop_history || []);
              if (m.crop !== p.crop || histChanged) {
                changed.push({ id: p.id, crop: m.crop, crop_label: label, crop_history: hist });
                return { ...p, crop: m.crop, crop_label: label, crop_history: hist };
              }
            }
            return p;
          });
          if (changed.length) {
            setProgress({ done: 0, total: changed.length, label: "Mise à jour des cultures" });
            let i = 0;
            for (const ch of changed) {
              await api.put(`/parcels/${ch.id}`, { crop: ch.crop, crop_label: ch.crop_label, crop_history: ch.crop_history });
              i++;
              setProgress({ done: i, total: changed.length, label: "Mise à jour des cultures" });
            }
            setProgress(null);
          }
        }

        setParcels(finalList);
        setShowParcels(true);
        setFitTrigger((t) => t + 1);
        const located = parsed.filter((p) => p.polygon).length;
        toast.success(`${parsed.length} parcelle(s) importée(s) — ${located} localisée(s).`);
      } catch (err) {
        setProgress(null);
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setImporting(false);
      }
    },
    [parcels]
  );

  const handleImport = useCallback(
    async (file) => {
      setImporting(true);
      try {
        const parsed = await parseFile(file);
        if (parsed.length === 0) {
          setImporting(false);
          toast.error("Aucune parcelle trouvée dans le fichier.");
          return;
        }
        if (parsed.some((p) => p.source === "xml")) {
          const detected =
            parsed.find((p) => p.campaign)?.campaign ||
            Number((file.name.match(/20\d{2}/) || [])[0]) ||
            new Date().getFullYear();
          setImporting(false);
          setCampaignDialog({ parsed, year: Number(detected) });
          return;
        }
        await processImport(parsed, null);
      } catch (err) {
        setImporting(false);
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
    },
    [processImport]
  );

  const handleSelect = useCallback((id) => {
    setSelectedId(id);
    setMobileOpen(false);
  }, []);

  const handleDeleteParcel = useCallback(async (id) => {
    try {
      await api.delete(`/parcels/${id}`);
      setParcels((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const handleClearAll = useCallback(async () => {
    if (!window.confirm("Supprimer toutes vos parcelles ? Cette action est irréversible.")) return;
    try {
      await api.delete("/parcels");
      setParcels([]);
      toast.success("Toutes les parcelles ont été supprimées.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const handleSaveFarm = async () => {
    try {
      const { data } = await api.put("/me/farm", { farm_name: farmName.trim() });
      updateUser({ ...user, farm_name: data.farm_name });
      setSettingsOpen(false);
      toast.success("Exploitation mise à jour.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleParcelSheet = useCallback(async (p) => {
    setSheetBusy(true);
    try {
      toast.info(`Génération de la fiche « ${p.name} »…`);
      await exportParcelSheet(p);
      toast.success("Fiche PDF téléchargée.");
    } catch {
      toast.error("Impossible de générer la fiche PDF.");
    } finally {
      setSheetBusy(false);
    }
  }, []);

  const handleOwnerSheet = useCallback(
    async (owner) => {
      setSheetBusy(true);
      try {
        toast.info(`Génération de la fiche de ${owner}…`);
        await exportOwnerSheet(owner, parcels);
        setOwnerDialogOpen(false);
        toast.success("Fiche propriétaire téléchargée.");
      } catch {
        toast.error("Impossible de générer la fiche PDF.");
      } finally {
        setSheetBusy(false);
      }
    },
    [parcels]
  );

  const handleOpenHistory = useCallback((p) => {
    setHistoryParcel(p);
    setHistEntries([...(p.crop_history || [])].sort((a, b) => b.year - a.year));
    setHistYear("");
    setHistCrop("");
  }, []);

  const handleAddHistEntry = () => {
    const y = parseInt(histYear, 10);
    if (!y || y < 1990 || y > 2100 || !histCrop.trim()) {
      toast.error("Indiquez une campagne valide (ex. 2025) et une culture.");
      return;
    }
    setHistEntries((prev) =>
      [{ year: y, crop: histCrop.trim(), crop_label: histCrop.trim() }, ...prev.filter((e) => e.year !== y)].sort(
        (a, b) => b.year - a.year
      )
    );
    setHistYear("");
    setHistCrop("");
  };

  const handleSaveHistory = async () => {
    try {
      const sorted = [...histEntries].sort((a, b) => a.year - b.year);
      const { data } = await api.put(`/parcels/${historyParcel.id}`, { crop_history: sorted });
      setParcels((prev) => prev.map((p) => (p.id === historyParcel.id ? data : p)));
      setHistoryParcel(null);
      toast.success("Historique des cultures enregistré.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleStartDraw = useCallback((parcel) => {
    setDrawParcel(parcel);
    setMobileOpen(false);
    toast.info("Cliquez sur la carte pour ajouter les sommets, double-cliquez pour terminer.");
  }, []);

  const handlePolygonDrawn = useCallback(
    async (pts) => {
      const parcel = drawParcel;
      setDrawParcel(null);
      if (!pts || !parcel) return;
      try {
        const { data } = await api.put(`/parcels/${parcel.id}`, { polygon: pts });
        setParcels((prev) => prev.map((p) => (p.id === parcel.id ? data : p)));
        setFitTrigger((t) => t + 1);
        toast.success(`Contour enregistré pour « ${parcel.name} ».`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    },
    [drawParcel]
  );

  const isAdmin = user?.role === "admin";

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-stone-100">
      <header className="z-[1100] flex h-14 shrink-0 items-center justify-between bg-[#1a4731] px-4 text-white shadow-md">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#e8c872] text-[#1a4731]">
            <Sprout size={20} />
          </span>
          <div className="leading-tight">
            <div className="text-base font-semibold" data-testid="farm-name">{user?.farm_name || "Mon Exploitation"}</div>
            <div className="text-[11px] text-[#e8c872]">Gestion parcellaire · Cadastre & Photo aérienne</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Button variant="ghost" size="sm" className="hidden text-white hover:bg-white/10 sm:inline-flex" onClick={() => navigate("/admin")} data-testid="admin-link">
              <ShieldCheck size={16} className="mr-1.5" /> Admin
            </Button>
          )}
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => { setFarmName(user?.farm_name || ""); setSettingsOpen(true); }} data-testid="settings-btn">
            <Settings size={18} />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
            <LogOut size={18} />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 md:hidden" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        <div className="relative z-0 h-full w-full flex-1">
          <MapView
            parcels={parcels}
            showAerial={showAerial}
            showCadastre={showCadastre}
            showParcels={showParcels}
            showCultures={showCultures}
            selectedId={selectedId}
            fitTrigger={fitTrigger}
            drawParcelId={drawParcel?.id || null}
            onPolygonDrawn={handlePolygonDrawn}
          />

          {/* Bandeau mode dessin */}
          {drawParcel && (
            <div className="absolute left-1/2 top-3 z-[1180] flex -translate-x-1/2 items-center gap-3 rounded-full bg-[#1a4731] px-4 py-2 text-sm text-white shadow-lg" data-testid="draw-banner">
              <Pencil size={15} className="text-[#e8c872]" />
              <span>Tracé de « {drawParcel.name} » — cliquez les sommets, double-clic pour terminer</span>
              <button onClick={() => setDrawParcel(null)} className="ml-2 rounded-full bg-white/15 px-2 py-0.5 text-xs hover:bg-white/25" data-testid="cancel-draw-btn">Annuler</button>
            </div>
          )}

          {/* Progression import / géocodage */}
          {progress && (
            <div className="absolute inset-0 z-[1185] flex items-center justify-center bg-black/30" data-testid="progress-overlay">
              <div className="w-80 rounded-xl bg-white p-5 shadow-2xl">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-stone-700">
                  <Loader2 size={16} className="animate-spin text-[#1a4731]" />
                  {progress.label}…
                </div>
                <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
                <div className="mt-2 text-right text-xs text-stone-500">{progress.done} / {progress.total}</div>
              </div>
            </div>
          )}
        </div>

        {mobileOpen && (
          <div className="absolute inset-0 z-[1190] bg-black/30 md:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />
        )}

        <div className={`absolute inset-y-0 right-0 z-[1200] w-[85%] max-w-[360px] transform border-l border-stone-200 shadow-2xl transition-transform duration-300 md:static md:w-[360px] md:translate-x-0 md:shadow-none ${mobileOpen ? "translate-x-0" : "translate-x-full md:translate-x-0"}`}>
          <Sidebar
            parcels={parcels}
            showAerial={showAerial} setShowAerial={setShowAerial}
            showCadastre={showCadastre} setShowCadastre={setShowCadastre}
            showParcels={showParcels} setShowParcels={setShowParcels}
            showCultures={showCultures} setShowCultures={setShowCultures}
            search={search} setSearch={setSearch}
            selectedId={selectedId} onSelect={handleSelect}
            onImport={handleImport} onDownloadTemplate={downloadTemplate}
            onExportPDF={() => exportPDF(parcels)} onExportExcel={() => exportExcel(parcels)}
            onDeleteParcel={handleDeleteParcel} onClearAll={handleClearAll}
            onStartDraw={handleStartDraw}
            onParcelSheet={handleParcelSheet}
            onOpenOwnerSheet={() => setOwnerDialogOpen(true)}
            onOpenHistory={handleOpenHistory}
            importing={importing}
          />
        </div>
      </div>

      <Dialog open={!!campaignDialog} onOpenChange={(o) => !o && setCampaignDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Campagne du fichier Télépac</DialogTitle>
            <DialogDescription>
              Les cultures de ce fichier seront enregistrées dans l'historique des parcelles pour cette campagne.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="campaign-input">Campagne (année)</Label>
            <Input
              id="campaign-input"
              type="number"
              min={2000}
              max={2100}
              value={campaignDialog?.year ?? ""}
              onChange={(e) => setCampaignDialog((c) => ({ ...c, year: e.target.value }))}
              data-testid="campaign-input"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                const y = parseInt(campaignDialog.year, 10);
                if (!y || y < 2000 || y > 2100) {
                  toast.error("Indiquez une année valide (ex. 2025).");
                  return;
                }
                const parsed = campaignDialog.parsed;
                setCampaignDialog(null);
                processImport(parsed, y);
              }}
              className="bg-[#1a4731] text-white hover:bg-[#143726]"
              data-testid="campaign-confirm-btn"
            >
              Importer la campagne
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyParcel} onOpenChange={(o) => !o && setHistoryParcel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Historique des cultures — {historyParcel?.name}</DialogTitle>
            <DialogDescription>Suivez la rotation des cultures campagne par campagne.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="max-h-52 space-y-1.5 overflow-y-auto" data-testid="history-list">
              {histEntries.length === 0 ? (
                <p className="text-sm text-stone-400">Aucune culture enregistrée pour cette parcelle.</p>
              ) : (
                histEntries.map((e) => (
                  <div
                    key={e.year}
                    className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
                    data-testid={`history-entry-${e.year}`}
                  >
                    <span className="font-semibold text-[#1a4731]">{e.year}</span>
                    <span className="flex-1 px-3 text-stone-700">{e.crop_label || e.crop}</span>
                    <button
                      onClick={() => setHistEntries((prev) => prev.filter((x) => x.year !== e.year))}
                      className="text-stone-300 transition-colors hover:text-red-600"
                      title="Supprimer"
                      data-testid={`history-delete-${e.year}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="flex items-end gap-2">
              <div className="w-24">
                <Label htmlFor="hist-year" className="text-xs">Campagne</Label>
                <Input id="hist-year" type="number" placeholder="2025" value={histYear} onChange={(e) => setHistYear(e.target.value)} data-testid="history-year-input" />
              </div>
              <div className="flex-1">
                <Label htmlFor="hist-crop" className="text-xs">Culture</Label>
                <Input id="hist-crop" placeholder="Blé tendre d'hiver" value={histCrop} onChange={(e) => setHistCrop(e.target.value)} data-testid="history-crop-input" />
              </div>
              <Button variant="outline" onClick={handleAddHistEntry} data-testid="history-add-btn">
                Ajouter
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveHistory} className="bg-[#1a4731] text-white hover:bg-[#143726]" data-testid="history-save-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fiche par propriétaire</DialogTitle>
            <DialogDescription>
              Choisissez un propriétaire pour générer sa fiche PDF : carte de ses parcelles + tableau récapitulatif.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 space-y-1.5 overflow-y-auto" data-testid="owner-list">
            {owners.length === 0 ? (
              <p className="text-sm text-stone-400">Aucun propriétaire renseigné dans vos parcelles.</p>
            ) : (
              owners.map((o) => (
                <button
                  key={o.name}
                  onClick={() => handleOwnerSheet(o.name)}
                  disabled={sheetBusy}
                  data-testid={`owner-sheet-item-${o.name.replace(/\s+/g, "-")}`}
                  className="flex w-full items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-left text-sm transition-colors hover:bg-stone-50 disabled:opacity-50"
                >
                  <span className="font-medium text-stone-800">{o.name}</span>
                  <span className="text-xs text-stone-500">
                    {o.count} parcelle(s){o.area ? ` · ${o.area} ha` : ""}
                  </span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réglages de l'exploitation</DialogTitle>
            <DialogDescription>Modifiez le nom affiché de votre exploitation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="farm-name-input">Nom de l'exploitation</Label>
            <Input id="farm-name-input" value={farmName} onChange={(e) => setFarmName(e.target.value)} data-testid="farm-name-input" />
            <p className="text-xs text-stone-400">Connecté en tant que {user?.email}</p>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveFarm} disabled={!farmName.trim()} className="bg-[#1a4731] text-white hover:bg-[#143726]" data-testid="save-farm-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
